import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useChangePasswordMutation } from "@/services";
import { toast } from "@/components/Toast";
import { routes } from "@/routes/utils";
import { getRequiredFields, removeEmptyFields } from "@/lib/utils";
import { schema } from "./validations";
import {
  CONFIRM_PASSWORD,
  CURRENT_PASSWORD,
  NEW_PASSWORD,
  TERMS_CONDITION,
} from "./consts";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useChangePassword = () => {
  const navigate = useNavigate();
  const [isPassword, setIsPassword] = useState<any>({
    current: true,
    newPassword: true,
    confirm: true,
  });

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const form = useForm<any>({
    defaultValues: {
      [CURRENT_PASSWORD]: "",
      [NEW_PASSWORD]: "",
      [CONFIRM_PASSWORD]: "",
      [TERMS_CONDITION]: true,
    },
    resolver: zodResolver(schema),
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const { mutate: changePasswordMutate, isPending: isChangePasswordPending } =
    useChangePasswordMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (loggedInUserData?.roles?.includes("ROLE_OWNER")) {
          navigate(`/${routes.owner}`);
        } else if (
          loggedInUserData?.roles?.includes("ROLE_CAM") ||
          loggedInUserData?.roles?.includes("ROLE_CAM_ACCOUNTANT")
        ) {
          navigate(`/${routes.cam}`);
        }
      },
    });

  const onSubmit = (data: any) => {
    const _data = removeEmptyFields(data);

    if (_data[NEW_PASSWORD] != _data[CONFIRM_PASSWORD]) {
      form.setError(CONFIRM_PASSWORD, {
        message: "New Password and Confirm Password must match.",
      });
      return;
    }

    if (!_data[TERMS_CONDITION]) {
      form.setError(TERMS_CONDITION, {
        message:
          "Please select the Terms and Conditions checkbox before proceeding.",
      });
      return;
    }
    delete _data[CONFIRM_PASSWORD];
    delete _data[TERMS_CONDITION];
    changePasswordMutate({ data: _data });
  };

  const onEyeIconClick = (key: string) => () => {
    setIsPassword({ ...isPassword, [key]: !isPassword[key] });
  };

  const Icon = isPassword ? Eye : EyeOff;

  return {
    navigate,
    form,
    requiredFields,
    onSubmit,
    onEyeIconClick,
    isPassword,
    Icon,
    isChangePasswordPending,
  };
};

export default useChangePassword;
